import { defineStore } from "pinia";
import {
  IDataProviderData,
  IDataProviderTableDefinition,
} from "@/interfaces/data-provider";
import { zipObject } from "lodash";
import { makeCategory } from "@/util/helpers";
import { $http } from "@/main";
import config from "@/util/constants";

interface MetadataCatalogState {
  selectedRecords: unknown[];
  selectedCategoryId: string;
  updateStatus: string;
  categories: Category[];
  editableFields: any;
  selectedDataCatId: any;
  updateLogId: any;

  dataCatUri: string;
  dataRefCatUri: string;
  updateCatUri: string;
  updateLogUri: string;
  dataDescCatUri: string;
  fieldCatUri: string;
  dateVars: string[];
  arrayTypeCols: string[];
  workflowBucket: string[];
}

interface Category {
  uri: string;
  id: string;
  label: string;
  meta: IDataProviderTableDefinition | null;
  data: IDataProviderData | null;
}

export const useMetadataCatalog = defineStore("metadataCatalog", {
  state: (): MetadataCatalogState => ({
    selectedRecords: [],
    selectedCategoryId: "",
    updateStatus: "",
    // categories: [
    //   "data_cat",
    //   "data_desc_cat",
    //   "field_cat",
    //   "field_publish_cat",
    //   "data_reference_cat",
    //   "provider_cat",
    //   "spatial_cat",
    //   "validation_cat",
    //   "update_cat",
    //   "al_cat",
    //   "v_update_log",
    //   "v_licence_cat",
    // ].map(makeCategory),
    categories: [
      "v_data_cat",
      "v_data_desc_cat",
      "v_field_cat",
      "v_field_publish_cat",
      "v_data_reference_cat",
      "v_provider_cat",
      "v_spatial_cat",
      "v_validation_cat",
      "v_update_cat",
      "v_al_cat",
      "v_update_log",
      "v_licence_cat",
    ].map(makeCategory),
    editableFields: {
      field_cat: [
        "is_primary_key",
        "is_spatial_field",
        "field_name",
        "field_nice_name",
        "field_short_desc",
        "field_long_desc",
        "calc_method_desc",
        "units_id",
        "is_critical_data",
        "data_type",
      ],
      data_cat: [
        "schema_id",
        "data_name_id",
        "region_id",
        "dataset_type_id",
        "data_status_id",
        "data_owner_team_id",
        "has_pii",
        "created_date",
        "has_geometry",
        "provider_cat_id",
        "dataset_name",
        "spatial_cat_id",
      ],
      data_desc_cat: [
        "high_level_desc",
        "detailed_desc",
        "creation_method_desc",
        "usage_rules_desc",
        "validity_time_amt",
        "validity_time_id",
        "project_id",
        "changes_gdpr_purpose",
        "ethical_level_id",
        "ethical_desc",
        "update_log_id",
      ],
    },
    selectedDataCatId: 0,
    updateLogId: 0,
    dataCatUri: "/meta_cat/1",
    dataRefCatUri: "/meta_cat/2",
    updateCatUri: "/meta_cat/3",
    updateLogUri: "/meta_cat/4",
    dataDescCatUri: "/meta_cat/5",
    fieldCatUri: "/meta_cat/6",
    dateVars: [
      "created_date",
      "updated_date",
      "last_update_due_date",
      "next_update_due_date",
      "update_due_date",
      "end_update_date",
    ],
    arrayTypeCols: ["new_version_num"],
    workflowBucket: [
      "v_data_reference_cat",
      "v_data_desc_cat",
      "v_update_cat",
      "v_update_log",
      "v_field_cat",
    ],
  }),
  getters: {
    selectedCategory(): Category {
      return (
        this.categories.find((c) => c.id === this.selectedCategoryId) ||
        this.categories[0]
      );
    },
    meta(): IDataProviderTableDefinition | null {
      return this.selectedCategory.meta;
    },
    data(): IDataProviderData | null {
      return this.selectedCategory.data;
    },
    columnNames(): string[] {
      return (this.meta?.columns || []).map((c) => c.columnName);
    },
    rows(): unknown[][] {
      return this.data?.rows || [];
    },
    items(): Record<string, unknown>[] {
      return this.rows.map((values) => zipObject(this.columnNames, values));
    },
  },
  actions: {
    updateMeta(id: string, meta: IDataProviderTableDefinition) {
      const index = this.categories.findIndex((c) => c.id === id);
      if (index > -1) {
        this.categories.splice(index, 1, {
          ...this.categories[index],
          meta,
        });
      }
    },
    updateData(id: string, data: IDataProviderData | null) {
      const index = this.categories.findIndex((c) => c.id === id);
      if (index > -1) {
        this.categories.splice(index, 1, {
          ...this.categories[index],
          data,
        });
      }
    },
    async updateMetadata(payload: {
      catalogUri: string;
      request: Record<string, unknown>;
    }): Promise<number> {
      const t = await $http.post(
        `${config.urls.data.getData}${payload.catalogUri}/update`,
        payload.request
      );
      return t.status;
    },
    async insertMetadata(payload: {
      catalogUri: string;
      request: Record<string, unknown>;
    }): Promise<number> {
      const t = await $http.post(
        `${config.urls.data.getData}${payload.catalogUri}/insert`,
        payload.request
      );
      return t.status;
    },
    getValOfKey(key: string, val: any): any {
      if (key.includes("_id") && typeof val.val === "string") {
        if (val?.val.includes(" - ")) {
          const v = val?.val.split(" - ")[0];
          return Number(v.trim());
        } else return val?.val;
      } else if (val.val === "false") {
        return false;
      } else if (val.val === "true") {
        return true;
      } else if (this.dateVars.includes(key)) {
        return new Date(new Date(val.val).toISOString());
      } else if (this.arrayTypeCols.includes(key)) {
        return val.val.split(",").map(function (num: string) {
          return parseInt(num);
        });
      } else {
        return val.val;
      }
    },
  },
});
